import { useState } from "react";
import { Alert, Button, Card, Descriptions, Modal, Spin, Tag, Timeline, Typography } from "@arco-design/web-react";

import { canUsePermission } from "../../../lib/auth";
import { useCurrentAuthUser } from "../../auth";
import {
  FINANCE_PAYMENT_REQUEST_ACCESS_PERMISSION,
  FINANCE_PAYMENT_REQUEST_CREATE_PERMISSION,
  FINANCE_PAYMENT_REQUEST_RECORD_PAYMENT_PERMISSION,
} from "../config";
import {
  useFinancePaymentRequestDetailRuntime,
  useFinancePaymentRequestWithdraw,
} from "../runtime/useFinancePaymentRequestDetailRuntime";
import { formatFinancePayableServiceDates } from "../service";
import {
  canRecordFinancePaymentRequestPayment,
  canResubmitFinancePaymentRequest,
  canWithdrawFinancePaymentRequest,
  describeFinancePaymentRequestStep,
  financeInvoiceStatusLabel,
  financePaymentRequestStatusLabel,
  financePaymentRequestLineTotal,
  financePaymentTypeLabel,
  formatFinancePaymentRequestAmount,
  formatFinancePaymentRequestApprovers,
  formatFinancePaymentRequestBank,
  formatFinancePaymentRequestStepApprovers,
  formatFinancePaymentRequestTime,
  financePaymentRequestResubmitFrom,
} from "../service/financePaymentRequestService";
import type { FinancePaymentRequestAttachment, FinancePaymentRequestDetail, FinancePaymentRequestPayment } from "../types";
import { FinancePaymentRecordModal, FinancePaymentVoidModal } from "./FinancePaymentRecordModals";
import { FinancePaymentRequestFormModal } from "./FinancePaymentRequestFormModal";
import "./financePaymentRequest.css";

type Props = {
  requestId: string;
  onBack: () => void;
};

/**
 * 请款详情：全部字段、勾的确认件行、附件（可下载）、每一轮审批记录、已付登记。
 * 有登记已付权限的人：看到完整收款账号；审批已通过、还没登记时可「登记已付」，有效那条登记可「作废这次登记」。
 * 申请人本人（且能发起请款）：审批中时可「撤回」，被驳回或撤回后可「修改后重新提交」。
 * 「返回请款记录」只给能看请款记录的人：从待我审批点进来的审批人不一定有这个权限，按钮通向他打不开的页面。
 */
export function FinancePaymentRequestDetailFeaturePage({ requestId, onBack }: Props) {
  const runtime = useFinancePaymentRequestDetailRuntime(requestId);
  const user = useCurrentAuthUser().data;
  const canRecord = canUsePermission(user, FINANCE_PAYMENT_REQUEST_RECORD_PAYMENT_PERMISSION);
  const canCreate = canUsePermission(user, FINANCE_PAYMENT_REQUEST_CREATE_PERMISSION);
  const [recording, setRecording] = useState(false);
  const [voidingPaymentId, setVoidingPaymentId] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);
  const back = canUsePermission(user, FINANCE_PAYMENT_REQUEST_ACCESS_PERMISSION)
    ? <Button onClick={onBack}>返回请款记录</Button>
    : null;
  if (runtime.loading) {
    return (
      <main className="finance-payment-request-page">
        <Card className="finance-payment-request-card">
          <Spin block className="finance-payment-request-loading" tip="正在读取…" />
        </Card>
      </main>
    );
  }
  if (!runtime.detail) {
    return (
      <main className="finance-payment-request-page">
        <Card className="finance-payment-request-card" extra={back}>
          <Alert type="error" content={runtime.error} />
        </Card>
      </main>
    );
  }
  const detail = runtime.detail;
  const canWithdraw = canCreate && canWithdrawFinancePaymentRequest(detail, user?.id);
  const canResubmit = canCreate && canResubmitFinancePaymentRequest(detail, user?.id);
  return (
    <main className="finance-payment-request-page">
      <Card
        className="finance-payment-request-card"
        title={`请款单 ${detail.requestNo}`}
        extra={
          <div className="finance-payment-request-footer">
            {canWithdraw ? <Button onClick={() => setWithdrawing(true)}>撤回</Button> : null}
            {canResubmit ? <Button type="primary" onClick={() => setResubmitting(true)}>修改后重新提交</Button> : null}
            {back}
          </div>
        }
      >
        <Descriptions column={2} data={summaryItems(detail)} />
      </Card>
      <Card className="finance-payment-request-card" title="依据确认件行">
        <LinesSnapshot detail={detail} />
      </Card>
      <Card className="finance-payment-request-card" title="收款账户">
        <Descriptions
          column={2}
          data={[
            { label: "收款户名", value: detail.payeeAccountName },
            { label: "开户行", value: formatFinancePaymentRequestBank(detail.payeeBankName, detail.payeeBankBranchName) },
            { label: "收款账号", value: detail.payeeAccountNo ?? detail.payeeAccountNoMasked },
          ]}
        />
      </Card>
      <Card className="finance-payment-request-card" title="附件">
        {runtime.downloadError ? <Alert type="error" content={runtime.downloadError} /> : null}
        <ul className="finance-payment-request-files">
          {detail.attachments.map((item) => (
            <li key={item.attachmentId}>
              <span>{item.fileName}</span>
              <Button type="text" size="mini" aria-label={`下载 ${item.fileName}`} onClick={() => void runtime.download(item)}>
                下载
              </Button>
            </li>
          ))}
        </ul>
      </Card>
      <Card
        className="finance-payment-request-card"
        title="已付登记"
        extra={
          canRecord && canRecordFinancePaymentRequestPayment(detail.status) ? (
            <Button type="primary" onClick={() => setRecording(true)}>登记已付</Button>
          ) : null
        }
      >
        <Payments
          payments={detail.payments}
          canVoid={canRecord}
          onVoid={setVoidingPaymentId}
          onDownload={(attachment) => void runtime.download(attachment)}
        />
      </Card>
      <Card className="finance-payment-request-card" title="审批记录">
        {detail.rounds.map((round) => (
          <section key={round.roundNo} className="finance-payment-request-round" data-testid="finance-payment-request-round">
            <Typography.Text bold>
              {`第 ${round.roundNo} 轮 · ${round.flowLabel} · ${financePaymentRequestStatusLabel(round.status)}`}
            </Typography.Text>
            <Timeline>
              {round.steps.map((step) => (
                <Timeline.Item key={step.stepNo}>
                  <div className="finance-payment-request-round-step">
                    <span>{`第 ${step.stepNo} 级`}</span>
                    <span>{formatFinancePaymentRequestStepApprovers(step)}</span>
                    <Typography.Text type={step.result === "rejected" ? "error" : "secondary"}>
                      {describeFinancePaymentRequestStep(round, step)}
                    </Typography.Text>
                    {step.remark ? <span>{`意见：${step.remark}`}</span> : null}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </section>
        ))}
      </Card>
      {recording ? (
        <FinancePaymentRecordModal requestId={detail.requestId} amount={detail.amount} onClose={() => setRecording(false)} />
      ) : null}
      {withdrawing ? <WithdrawModal requestId={detail.requestId} onClose={() => setWithdrawing(false)} /> : null}
      {resubmitting ? (
        <FinancePaymentRequestFormModal
          {...financePaymentRequestResubmitFrom(detail)}
          onClose={() => setResubmitting(false)}
        />
      ) : null}
      {voidingPaymentId ? (
        <FinancePaymentVoidModal
          requestId={detail.requestId}
          paymentId={voidingPaymentId}
          onClose={() => setVoidingPaymentId(null)}
        />
      ) : null}
    </main>
  );
}

/** 撤回确认：撤回后不占额度，可以改完重新提交；能不能撤由审批模块判断，拦下的原因原样显示。 */
function WithdrawModal({ requestId, onClose }: { requestId: string; onClose: () => void }) {
  const withdraw = useFinancePaymentRequestWithdraw(requestId);
  return (
    <Modal
      title="撤回请款"
      visible
      style={{ width: 480 }}
      maskClosable={!withdraw.submitting}
      onCancel={() => { if (!withdraw.submitting) onClose(); }}
      unmountOnExit
      footer={
        <div className="finance-payment-request-footer">
          <Button onClick={onClose} disabled={withdraw.submitting}>取消</Button>
          <Button
            type="primary"
            status="danger"
            loading={withdraw.submitting}
            onClick={() => void withdraw.withdraw().then((done) => { if (done) onClose(); })}
          >
            确认撤回
          </Button>
        </div>
      }
    >
      <div className="finance-payment-request-form">
        <span className="finance-payment-request-muted">
          撤回后这笔请款不再占可请额度，可以改完重新提交；第一级审批人已经处理过的不能撤回。
        </span>
        {withdraw.error ? <Alert type="error" content={withdraw.error} /> : null}
      </div>
    </Modal>
  );
}

/** 全部已付登记（从新到旧）：作废了的写明谁、什么时候作废和原因；有效那条在有权限时可作废。各带付款回单。 */
function Payments({
  payments,
  canVoid,
  onVoid,
  onDownload,
}: {
  payments: FinancePaymentRequestPayment[];
  canVoid: boolean;
  onVoid: (paymentId: string) => void;
  onDownload: (attachment: FinancePaymentRequestAttachment) => void;
}) {
  if (payments.length === 0) return <span className="finance-payment-request-muted">还没有登记已付。</span>;
  return (
    <div className="finance-payment-request-payments">
      {payments.map((item) => (
        <section key={item.paymentId} className="finance-payment-request-payment" data-testid="finance-payment-request-payment">
          <div className="finance-payment-request-payment-head">
            <Tag color={item.voidedAt ? "gray" : "green"}>{item.voidedAt ? "已作废" : "有效"}</Tag>
            {!item.voidedAt && canVoid ? (
              <Button size="mini" status="danger" onClick={() => onVoid(item.paymentId)}>作废这次登记</Button>
            ) : null}
          </div>
          <Descriptions
            column={2}
            data={[
              { label: "付款金额", value: formatFinancePaymentRequestAmount(item.amount) },
              { label: "付款日期", value: item.paymentDate },
              { label: "本方付款账户", value: item.payerAccount },
              { label: "银行流水号", value: item.bankSerialNo },
              { label: "备注", value: item.remark ?? "—" },
              { label: "登记", value: `${item.recordedByName} · ${formatFinancePaymentRequestTime(item.recordedAt)}` },
            ]}
          />
          {item.voidedAt ? (
            <Typography.Text type="secondary">
              {`作废：${item.voidedByName ?? "—"} · ${formatFinancePaymentRequestTime(item.voidedAt)} · 原因：${item.voidReason ?? "—"}`}
            </Typography.Text>
          ) : null}
          <ul className="finance-payment-request-files">
            {item.attachments.map((attachment) => (
              <li key={attachment.attachmentId}>
                <span>{attachment.fileName}</span>
                <Button type="text" size="mini" aria-label={`下载 ${attachment.fileName}`} onClick={() => onDownload(attachment)}>
                  下载
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** 勾的确认件行（提交时的快照）和合计；没勾的写明直接填的金额。 */
function LinesSnapshot({ detail }: { detail: FinancePaymentRequestDetail }) {
  const total = financePaymentRequestLineTotal(detail.lines, detail.lines.map((line) => line.lineId));
  if (!total) return <span className="finance-payment-request-muted">没有勾确认件行，直接填的金额。</span>;
  return (
    <>
      <ul className="finance-payment-request-line-list">
        {detail.lines.map((line) => (
          <li key={line.lineId}>
            <span className="finance-payment-request-line">
              <span>{`第 ${line.lineNo} 行 ${line.itemName}${line.itemSpec ? ` · ${line.itemSpec}` : ""}`}</span>
              <span className="finance-payment-request-money">{formatFinancePaymentRequestAmount(line.amount)}</span>
            </span>
          </li>
        ))}
      </ul>
      <span className="finance-payment-request-muted">{`勾选合计 ${formatFinancePaymentRequestAmount(total)}（${detail.lines.length} 行）`}</span>
    </>
  );
}

/** 请款单的业务字段。 */
function summaryItems(detail: FinancePaymentRequestDetail) {
  return [
    { label: "状态", value: <Tag>{financePaymentRequestStatusLabel(detail.status)}</Tag> },
    { label: "当前审批人", value: formatFinancePaymentRequestApprovers(detail.currentApprovers) },
    { label: "团", value: detail.itineraryTitle },
    { label: "服务日期", value: formatFinancePayableServiceDates(detail.serviceDateFrom, detail.serviceDateTo) },
    { label: "结算主体", value: detail.settlementSupplierName },
    { label: "款项类型", value: financePaymentTypeLabel(detail.paymentType) },
    { label: "本次请款金额", value: formatFinancePaymentRequestAmount(detail.amount) },
    { label: "期望付款日期", value: detail.expectedPaymentDate },
    { label: "票据状态", value: financeInvoiceStatusLabel(detail.invoiceStatus) },
    { label: "所选部门", value: detail.departmentName },
    { label: "请款用途", value: detail.purpose },
    { label: "备注", value: detail.remark ?? "—" },
    { label: "申请人", value: detail.applicantName },
    { label: "提交时间", value: formatFinancePaymentRequestTime(detail.submittedAt) },
  ];
}
